import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, UtensilsCrossed, HandHeart, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import crown from "@/assets/crown.png";
import royals from "@/assets/royals.png";

const STEPS = [
  {
    icon: UtensilsCrossed,
    title: "Plan",
    text: "A Planner sponsors a table — cafe, fine dining, heritage tea — and pays the bill at the venue.",
  },
  {
    icon: HandHeart,
    title: "Join",
    text: "Joinees request a seat. The Planner reviews profiles and selects one guest of honour.",
  },
  {
    icon: Sparkles,
    title: "Meet",
    text: "Two strangers, one exquisite meal, somewhere beautiful in Kolkata.",
  },
];

export default function Landing() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const enter = () => (user ? navigate("/circle") : login());

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white">
      <header className="sticky top-0 z-40 bg-[#0B0B0F]/80 backdrop-blur-xl border-b border-[#D4AF37]/20">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={crown} alt="" className="w-8 h-8" />
            <span className="font-serif font-bold text-lg tracking-wide text-[#D4AF37]">THE CIRCLE</span>
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/how-it-works"
              data-testid="landing-how-it-works-link"
              className="text-sm text-[#E0E0E0] hover:text-[#D4AF37] transition-colors"
            >
              How It Works
            </Link>
            <Button
              onClick={enter}
              data-testid="landing-signin-button"
              size="sm"
              className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md transition-colors"
            >
              {user ? "Enter" : "Sign In"}
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-20 pb-16 flex flex-col items-center text-center">
        <img src={crown} alt="" className="w-24 h-24 crown-glow mb-6 fade-up" />
        <h1
          className="font-serif text-5xl sm:text-6xl font-bold tracking-tight text-white fade-up"
          style={{ animationDelay: "80ms" }}
          data-testid="landing-hero-title"
        >
          THE CIRCLE
        </h1>
        <div className="gold-divider w-32 my-7 fade-up" style={{ animationDelay: "160ms" }} />
        <p
          className="text-base md:text-lg text-[#E0E0E0] max-w-md leading-relaxed fade-up"
          style={{ animationDelay: "240ms" }}
        >
          Kolkata's private table. Curated meals sponsored by Planners,
          shared with kindred spirits.
        </p>
        <img
          src={royals}
          alt="Prince and Queen"
          className="w-64 sm:w-72 my-10 fade-up"
          style={{ animationDelay: "320ms" }}
          data-testid="landing-royals-graphic"
        />
        <div className="flex flex-col sm:flex-row items-center gap-4 fade-up" style={{ animationDelay: "400ms" }}>
          <Button
            onClick={enter}
            data-testid="landing-google-login-button"
            className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md px-8 h-12 text-base transition-colors"
          >
            {user ? "Enter the Circle" : "Continue with Google"}
            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
          </Button>
          <Link to="/how-it-works">
            <Button
              variant="outline"
              data-testid="landing-how-cta"
              className="border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F1D570] bg-transparent rounded-md px-8 h-12 text-base transition-colors"
            >
              See How It Works
            </Button>
          </Link>
        </div>
      </section>

      <section className="border-t border-[#D4AF37]/15">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-10 text-center">The Ritual</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className="bg-[#15151A] border border-[#D4AF37]/20 rounded-xl p-7 fade-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <Icon className="w-7 h-7 text-[#D4AF37] mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-xl font-semibold mb-2">{title}</h3>
                <p className="text-sm text-[#A0A0A0] leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#A0A0A0] mt-12">
            Commitment is sealed with deposits — Planner 40%, Joinee 25%, refunded after the meetup.{" "}
            <Link to="/how-it-works" className="text-[#D4AF37] hover:text-[#F1D570] transition-colors underline underline-offset-4">
              Learn more
            </Link>
          </p>
        </div>
      </section>

      <footer className="border-t border-[#D4AF37]/15 py-10 text-center">
        <p className="text-xs uppercase tracking-[0.35em] text-[#A0A0A0]">
          THE CIRCLE · Exclusively Kolkata
        </p>
      </footer>
    </div>
  );
}
